import { createSelector } from "@reduxjs/toolkit"
import { familyMembersSelector } from "features/team/model/selectors/teamSelectors"
import { IMember } from '@cloudike/web_photos/dist/types/intarfaces/IMember'

import { ANNOTATION_TYPE } from '../../ui/utils/annotationUtils'
import { filesPreviewSelectors } from "../slice/filesPreviewSlice"

const getAvatarUrl = (member: IMember) => member && member?._links?.avatar?.href || ''

export const getFilesPreviewItemsSelector = state => filesPreviewSelectors.selectAll(state.filesPreview)

export const getCurrentFilesPreviewItemIndexSelector = state => state.filesPreview.currentItemIndex

export const getFilesPreviewTypeSelector = state => state.filesPreview.type

export const getCurrentFilesPreviewModalStatusSelector = state => state.filesPreview.isModalOpened

export const getFilesPreviewLoadingSelector = state => state.filesPreview.loading

export const getFilesPreviewLoadingMoreSelector = state => state.filesPreview.loadingMore

export const getFilesPreviewErrorSelector = state => state.filesPreview.hasError

export const getFilesPreviewTotalItemsCountSelector = state => state.filesPreview.totalItemsCount

export const getFilesPreviewAiTagsSelector = state => state.filesPreview.aiTags

export const getFilesPreviewSystemFieldsValuesSelector = state => state.filesPreview.systemFieldsValues

export const getFilesPreviewSystemFieldsOptionsSelector = state => state.filesPreview.systemFieldsOptions

export const getFilesPreviewFieldsIdsSelector = state => state.filesPreview.fieldsIds

export const getFilesPreviewCommentsSelector = state => state.filesPreview.comments

export const getFilesPreviewTabsContentLoadedSelector = state => state.filesPreview.tabsContentLoaded

export const getFilesPreviewCurrentBoardInfoSelector = state => state.filesPreview.currentBoardInfo

export const getFilesPreviewIsPublicLinkSelector = state => state.filesPreview.isPublicLink

export const getFilesPreviewIsCommentsLoadingSelector = state => state.filesPreview.isCommentsLoading

export const getFilesPreviewIsAnnotatingSelector = state => state.filesPreview.isAnnotating

export const getFilesPreviewWithPreventedClosingSelector = state => state.filesPreview.withPreventedClosing

export const getFilesPreviewCommentsFilterSelector = state => state.filesPreview.commentsFilter

export const getFilesPreviewAnnotationsSelector = createSelector(
  getFilesPreviewCommentsSelector,
  getFilesPreviewCommentsFilterSelector,
  (comments, filter) => {
    const filterComments = (commentsList: any[]): any[] => {
      return commentsList.filter(comment => {
        let shouldInclude = false

        switch (filter) {
        case 'all':
          shouldInclude = true
          break
        case 'open':
          shouldInclude = comment.status === 'open'
          break
        case 'resolved':
          shouldInclude = comment.status === 'resolved'
          break
        default:
          shouldInclude = true
        }

        return shouldInclude
      }).map(comment => {
        const newComment = { ...comment }
        if (newComment.replies) {
          newComment.replies = filterComments(newComment.replies)
        }
        return newComment
      })
    }

    const filteredComments = filterComments([...comments])

    return filteredComments.reduce((acc, comment) => {
      if (comment.annotation) {
        acc.push(comment)
      }
      return acc
    }, [])
  }
)

export const getFilesPreviewAnnotationsByOffsetSelector = (offset = 0) => createSelector(
  getFilesPreviewAnnotationsSelector,
  (annotationsData) => {
    return annotationsData
      .filter(comment => comment.annotation.offset === offset)
  }
)

export const getFilesPreviewAnnotationsGeometryByOffsetSelector = (offset = 0) => createSelector(
  getFilesPreviewAnnotationsByOffsetSelector(offset),
  familyMembersSelector,
  (annotationsData, familyMembers) => {
    const annotationsGeometry = annotationsData.map((comment) => {
      const member = familyMembers.find((member) => Number(member.id) === Number(comment.author_id))
      const avatarUrl = getAvatarUrl(member)
      const coordinates = comment.annotation.coordinates
      const data = { text: comment.message, id: comment.id, authorId: comment.author_id, author_avatar_url: avatarUrl, ...comment }
      switch (comment.annotation.type) {
      case ANNOTATION_TYPE.RECTANGLE:
        return {
          type: comment.annotation.type,
          x: coordinates[0],
          y: coordinates[1],
          width: coordinates[2] - coordinates[0],
          height: coordinates[3] - coordinates[1],
          data,
        }
      case ANNOTATION_TYPE.POINT:
        return {
          type: comment.annotation.type,
          x: coordinates[0],
          y: coordinates[1],
          data,
        }
      }
    })
    return annotationsGeometry
  }
)

export const getSelectedAnnotationSelector = state => state.filesPreview.selectedAnnotation

export const getNewAnnotationSelector = state => state.filesPreview.newAnnotation

export const getPlayerStateSelector = state => state.filesPreview.playerState

export const getPlayerCurrentTimeSelector = state => state.filesPreview.playerState.currentTime

export const getPlayerDurationSelector = state => state.filesPreview.playerState.duration

export const getPlayerIsPlayingSelector = state => state.filesPreview.playerState.isPlaying

export const getPlayerVolumeSelector = state => state.filesPreview.playerState.volume

export const getPlayerIsMutedSelector = state => state.filesPreview.playerState.isMuted

export const getPlayerIsFullscreenSelector = state => state.filesPreview.playerState.isFullscreen

export const getPlayerIsLoopingSelector = state => state.filesPreview.playerState.isLooping

export const getPlayerPlaybackRateSelector = state => state.filesPreview.playerState.playbackRate

export const getFilesPreviewActiveTabSelector = state => state.filesPreview.activeTab

export const getIsShowAnnotationLayerSelector = state => state.filesPreview.isShowAnnotationLayer

export const getIsShowAnnotationMarkersLayerSelector = state => state.filesPreview.isShowAnnotationMarkers

export const getIsCreatingNewAnnotationSelector = state => state.filesPreview.isCreatingNewAnnotation

export const getCreatingNewAnnotationModeSelector = state => state.filesPreview.creatingNewAnnotationMode

export const getFilesPreviewCommentIdForScrollToSelector = state => state.filesPreview.commentIdForScrollTo

export const getReplyingCommentSelector = state => state.filesPreview.replyingCommentId

export const getFilesPreviewOverlayImageDataSelector = state => state.filesPreview.overlayImageData

export const getFilesPreviewSourceCommentIdSelector = state => state.filesPreview.overlayImageData.sourceCommentId

export const getFilesPreviewIsControlPanelVisibleSelector = state => state.filesPreview.isControlPanelVisible

export const getFilteredCommentsSelector = createSelector(
  getFilesPreviewCommentsSelector,
  getFilesPreviewCommentsFilterSelector,
  familyMembersSelector,
  (comments, filter, familyMembers) => {
    const filterComments = (commentsList: any[], parentStatus?: 'open' | 'resolved'): any[] => {
      return commentsList.filter(comment => {
        const effectiveStatus = parentStatus || comment.status || 'open'

        let shouldInclude = false

        switch (filter) {
        case 'all':
          shouldInclude = true
          break
        case 'open':
          shouldInclude = effectiveStatus === 'open'
          break
        case 'resolved':
          shouldInclude = effectiveStatus === 'resolved'
          break
        default:
          shouldInclude = true
        }

        return shouldInclude
      }).map(comment => {
        const newComment = { ...comment }
        const currentStatus = parentStatus || comment.status || 'open'
        if (newComment.replies) {
          newComment.replies = filterComments(newComment.replies, currentStatus)
        }
        return newComment
      }).map((comment) => {
        const member = familyMembers.find((member) => Number(member.id) === Number(comment.author_id))
        const replies = comment.replies?.map((reply) => {
          const replyMember = familyMembers.find((member) => Number(member.id) === Number(reply.author_id))
          return {
            ...reply,
            avatarUrl: getAvatarUrl(replyMember)
          }
        })
        return {
          ...comment,
          replies,
          avatarUrl: getAvatarUrl(member)
        }
      })
    }

    return filterComments([...comments])
  }
)

export const getCommentsCountByStatusSelector = createSelector(
  getFilesPreviewCommentsSelector,
  (comments) => {
    const countComments = (commentsList: any[], parentStatus?: 'open' | 'resolved') => {
      let openCount = 0
      let resolvedCount = 0

      commentsList.forEach(comment => {
        const effectiveStatus = parentStatus || comment.status || 'open'

        if (effectiveStatus === 'open') {
          openCount++
        } else if (effectiveStatus === 'resolved') {
          resolvedCount++
        }

        if (comment.replies) {
          const replyCounts = countComments(comment.replies, effectiveStatus)
          openCount += replyCounts.open
          resolvedCount += replyCounts.resolved
        }
      })

      return { open: openCount, resolved: resolvedCount }
    }

    const counts = countComments(comments)
    return {
      open: counts.open,
      resolved: counts.resolved,
      all: counts.open + counts.resolved
    }
  }
)

export const getFilesPreviewModalTypeSelector = state => state.filesPreview.modalType